import '@arcgis/core/assets/esri/themes/dark/main.css';

import {
  createContext,
  Dispatch,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useRef,
  SetStateAction,
  useState,
  MutableRefObject,
} from 'react';
import { matchPath, useLocation } from 'react-router';
import MapView from '@arcgis/core/views/MapView';
import WebMap from '@arcgis/core/WebMap';
import { HYBRID } from 'components/MapPage/CorporateViewTabs/LayersTab/arcgis-integration/arcGisTypes';
import { useCurrentGroup } from 'components/IAP/hooks/commonHooks';
import { useListExternalLayersQuery } from 'hooks/externalLayer/useListExternalLayersQuery';
import { useListInternalLayersQuery } from 'hooks/internalLayer/useListInternalLayersQuery';
import {
  UscgSymbolDrawingReturnType,
  useUscgSymbolDrawing,
} from './utils/useUscgSymbolDrawing';
import { useInternalLayerSync } from './utils/useInternalLayerSync';
import { useDefaultInternalLayerVersionSelection } from './utils/useDefaultInternalLayerVersionSelection';
import {
  zoomMapViewToLocation,
  zoomMapViewToGraphic,
} from './utils/mapViewZoom';
import {
  queryExternalLayerFeatures,
  type ExternalLayerFeature,
} from './utils/queryExternalLayerFeatures';
import { useUscgAorLayerSync } from './utils/useUscgAorLayerSync';
import { useExternalLayerSync } from './utils/useExternalLayerSync';
import type {
  ExternalLayer,
  ExternalLayerAccessType,
} from 'hooks/externalLayer/types';
import type { InternalLayer } from 'hooks/internalLayer/types';
import type { InternalLayerVersion } from 'hooks/internalLayerVersion/types';
import FeatureLayer from '@arcgis/core/layers/FeatureLayer';
import GroupLayer from '@arcgis/core/layers/GroupLayer';
import MapImageLayer from '@arcgis/core/layers/MapImageLayer';
import GraphicsLayer from '@arcgis/core/layers/GraphicsLayer';
import { useExactIncident } from 'components/IAP/hooks/incidentHooks';
import { useLocationGraphicLayer } from './utils/useLocationGraphicLayer';
import { US_INITIAL_VIEW } from './utils/constants';
import * as reactiveUtil from '@arcgis/core/core/reactiveUtils';
import { useGetRostersForGroup } from 'hooks/incident/useRoster';
import { useAors, useAorDescendants } from 'hooks/useAors';
import {
  selectGroupGuid,
  selectUserGuid,
  useAppSelector,
} from 'slices/commonSelectors';
import { performZoomToHomeAor } from 'components/MapPage/CorporateViewTabs/AorsTab/utils/useAutoZoomToHomeAor';
import { useArcGisAuthorization } from './utils/useArcGisAuthorization';
import { useDefaultLayerVisibilityById } from './utils/useDefaultLayerVisibilityById';

const USCG_PORTAL_ITEM_ID = '7899d4c1ae2a4eaea752e48c17b62f0b';
const MAP_BENCH_ID = 'map-bench';
const MAP_BENCH_WIDTH = 400;
const MAP_BENCH_HEIGHT = 400;

export type UscgMapType = 'uscg' | 'hybrid';

type PreviewLayer = {
  id: string | nullish;
  url: string | nullish;
  authType?: ExternalLayerAccessType;
};

type UscgMapContextType = {
  mapView: MapView | null;
  mapViewElementRef: MutableRefObject<HTMLDivElement | null>;
  isMapVisible: boolean;
  setIsMapVisible: Dispatch<SetStateAction<boolean>>;
  mapType: UscgMapType;
  setMapType: (mapType: UscgMapType) => void;
  toggleExternalLayerVisibility: (layerId: string) => void;
  isExternalLayerVisible: (layerId: string) => boolean;
  toggleInternalLayerVisibility: (layerId: string) => void;
  isInternalLayerVisible: (layerId: string) => boolean;
  // Shared AOR filter (applied to both sidebar and map)
  aorFilterIds: string[] | null;
  setAorFilterIds: Dispatch<SetStateAction<string[] | null>>;

  incidentFilterId: string | null;
  setIncidentFilterId: (id: string | null) => void;

  toggleAorVisibility: () => void;
  isAorVisible: boolean;
  setIsAorVisible: (visible: boolean) => void;

  zoomToLocation: (params: {
    longitude: number;
    latitude: number;
    zoom?: number;
  }) => Promise<void> | undefined;

  zoomToGraphic: (params: {
    graphic: __esri.Graphic;
    zoom?: number;
  }) => Promise<void> | undefined;
  zoomToExternalLayer: (params: {
    externalLayerId: string;
  }) => Promise<void> | undefined;
  zoomToInternalLayer: (params: {
    internalLayerId: string;
  }) => Promise<void> | undefined;

  getExternalLayerFeatures: (
    externalLayerId: string
  ) => Promise<ExternalLayerFeature[]>;
  isExternalLayerLoadError: (externalLayerId: string) => boolean;
  externalLayerLoadErrorById: Map<string, boolean>;

  selectedInternalLayerVersionByInternalLayerId: Record<
    string,
    InternalLayerVersion
  >;
  getSelectedVersionForLayer: (
    layerId: string
  ) => InternalLayerVersion | undefined;
  setSelectedInternalLayerVersion: (
    layerId: string,
    version: InternalLayerVersion
  ) => void;
  sidebarSelectedInternalLayer: InternalLayer | null;
  setSidebarSelectedInternalLayer: (layer: InternalLayer | null) => void;
  isLoadingInternalFeatures: boolean;
  isArcGISAuthorized: boolean;
  isCheckingArcGISAuthorization: boolean;
  authorizeArcGIS: () => Promise<void>;
  setIsArcGISAuthorized: (authorized: boolean) => void;
  isPublicExternalLayersReady: boolean;
  isProtectedExternalLayersReady: boolean;
  isExternalLayersReady: boolean;
  captureMapSketchFile: () => Promise<File | undefined>;

  previewLayers: PreviewLayer[];
  setPreviewLayers: Dispatch<SetStateAction<PreviewLayer[]>>;
} & Pick<
  UscgSymbolDrawingReturnType,
  | 'startDrawing'
  | 'setPolygonDrawingColor'
  | 'setPolygonDrawingOpacity'
  | 'stopDrawing'
  | 'addPendingPoint'
  | 'addPendingPolyline'
  | 'addPendingPolygon'
  | 'clearPendingGraphics'
  | 'removePendingGraphicsByPendingGraphicId'
  | 'symbolDrawingState'
  | 'connectDrawingPoints'
  | 'resetDrawing'
>;

const UscgMapContext = createContext<UscgMapContextType | undefined>(undefined);

export const useUscgMapContext = (): UscgMapContextType => {
  const context = useContext(UscgMapContext);
  if (context === undefined) {
    throw new Error(
      'useUscgMapContext must be used within a UscgMapContextProvider'
    );
  }

  return context;
};

export const UscgMapContextProvider: React.FC<React.PropsWithChildren> = ({
  children,
}) => {
  const mapViewElementRef = useRef<HTMLDivElement | null>(null);
  const addedExternalLayersRef = useRef<Map<string, __esri.Layer[]>>(new Map());
  // Keep track of state that shouldn't trigger a re-render, prevent useEffect infinite loop.
  // The Map object from ArcGIS Maps SDK will handle the visual updates
  const internalGraphicsLayersRef = useRef<Map<string, __esri.GraphicsLayer>>(
    new Map()
  );
  const locationGraphicLayerRef = useRef<GraphicsLayer>(
    new GraphicsLayer({ title: 'Incident Location' })
  );

  const [mapView, setMapView] = useState<MapView | null>(null);
  const [isMapVisible, setIsMapVisible] = useState(true);
  const [mapType, setMapType] = useState<UscgMapType>('hybrid');
  const [externalLayerVisibilityById, setExternalLayerVisibilityById] =
    useState<Map<string, boolean | null>>(() => new Map());
  const [externalLayerLoadErrorById, setExternalLayerLoadErrorById] = useState<
    Map<string, boolean>
  >(() => new Map());
  const [internalLayerVisibilityById, setInternalLayerVisibilityById] =
    useState<Map<string, boolean | null>>(() => new Map());
  const [isAorVisible, setIsAorVisible] = useState(false);
  const [aorFilterIds, setAorFilterIds] = useState<string[] | null>(null);
  const [stateIncidentFilterId, setIncidentFilterId] = useState<string | null>(
    null
  );
  const [
    selectedInternalLayerVersionByInternalLayerId,
    setSelectedInternalLayerVersionByInternalLayerId,
  ] = useState<Record<string, InternalLayerVersion>>({});
  const [sidebarSelectedInternalLayer, setSidebarSelectedInternalLayer] =
    useState<InternalLayer | null>(null);
  const {
    isArcGISAuthorized,
    isCheckingArcGISAuthorization,
    authorizeArcGIS,
    setIsArcGISAuthorized,
  } = useArcGisAuthorization();

  const [isPublicExternalLayersReady, setIsPublicExternalLayersReady] =
    useState(false);
  const [isProtectedExternalLayersReady, setIsProtectedExternalLayersReady] =
    useState(false);
  const [isExternalLayersReady, setIsExternalLayersReady] = useState(false);

  const [previewLayers, setPreviewLayers] = useState<PreviewLayer[]>([]);

  const userGuid = useAppSelector(selectUserGuid);
  const groupGuid = useAppSelector(selectGroupGuid);
  const { data: roster } = useGetRostersForGroup(groupGuid);
  const { data: aors } = useAors();

  const currentUserRoster = roster?.find((r) => r.user_guid === userGuid);
  const homeAorId = currentUserRoster?.home_aor ?? null;
  const { data: homeAorDescendants, isLoading: isHomeAorDescendantsLoading } =
    useAorDescendants(homeAorId);

  const {
    startDrawing,
    setPolygonDrawingColor,
    setPolygonDrawingOpacity,
    stopDrawing,
    addPendingPoint,
    addPendingPolyline,
    addPendingPolygon,
    clearPendingGraphics,
    removePendingGraphicsByPendingGraphicId,
    symbolDrawingState,
    connectDrawingPoints,
    resetDrawing,
  } = useUscgSymbolDrawing({
    mapView,
  });

  // get :incidentId from the url when available in Incident Workspace
  // we cannot use useParams because UscgMapContextProvider wraps all Routes, it is not a child of any one Route
  const location = useLocation();
  const iapMatch = matchPath('/iap/:incidentId/*', location.pathname);
  const incidentIdParam = iapMatch?.params.incidentId;
  const incidentFilterId = incidentIdParam ?? stateIncidentFilterId;
  const { data: incident } = useExactIncident(incidentFilterId ?? '');
  const group = useCurrentGroup();
  const { data: publicExternalLayersData } = useListExternalLayersQuery({
    filter: {
      authType: 'apiKey',
      groupGuid: group.group_guid ?? '',
      incidentId: incidentFilterId || undefined,
    },
    perPage: 200,
    include: ['incident'],
  });

  const { data: orgExternalLayersData } = useListExternalLayersQuery({
    filter: {
      authType: 'userAuth',
      groupGuid: group.group_guid ?? '',
      incidentId: incidentFilterId || undefined,
    },
    perPage: 200,
    include: ['incident'],
  });

  const { data: internalLayersData } = useListInternalLayersQuery({
    filter: {
      groupGuid: group.group_guid ?? '',
    },
    perPage: 200,
    include: ['incident'],
  });

  const { isLoading: isLoadingInternalFeatures } = useInternalLayerSync({
    mapView,
    internalLayerVisibilityById,
    incidentFilterId: incidentFilterId ?? null,
    selectedInternalLayerVersionByInternalLayerId,
    layersRef: internalGraphicsLayersRef,
  });

  useDefaultInternalLayerVersionSelection({
    setSelectedInternalLayerVersionByInternalLayerId,
  });

  // Sync AOR geometries into the USCG map, respecting AOR filter
  useUscgAorLayerSync({ mapView, isVisible: isAorVisible, aorFilterIds });

  const publicExternalLayers = useMemo(() => {
    return publicExternalLayersData?.data ?? [];
  }, [publicExternalLayersData]);

  const orgExternalLayers = useMemo(() => {
    if (!isArcGISAuthorized) {
      return [];
    }

    return orgExternalLayersData?.data ?? [];
  }, [isArcGISAuthorized, orgExternalLayersData]);

  const internalLayersWithVisibilityDefaults = useMemo<
    Pick<InternalLayer, 'id' | 'defaultVisible'>[]
  >(() => internalLayersData?.data ?? [], [internalLayersData]);

  const previewExternalLayers = useMemo<
    Pick<
      ExternalLayer,
      | 'id'
      | 'arcgisItemId'
      | 'serviceUrl'
      | 'incident'
      | 'authType'
      | 'defaultVisible'
    >[]
  >(
    () =>
      previewLayers
        .filter(
          (
            layer
          ): layer is PreviewLayer & {
            id: string;
            authType: ExternalLayerAccessType;
          } => Boolean(layer.id && layer.authType)
        )
        .map((layer) => ({
          id: layer.id,
          arcgisItemId: layer.id,
          serviceUrl: layer.url,
          incident: null,
          authType: layer.authType,
          defaultVisible: true, // Preview layers default to visible
        })),
    [previewLayers]
  );

  const externalLayersToSync = useMemo<
    Pick<
      ExternalLayer,
      | 'id'
      | 'arcgisItemId'
      | 'serviceUrl'
      | 'incident'
      | 'authType'
      | 'defaultVisible'
    >[]
  >(() => {
    const merged = new Map<
      string,
      Pick<
        ExternalLayer,
        | 'id'
        | 'arcgisItemId'
        | 'serviceUrl'
        | 'incident'
        | 'authType'
        | 'defaultVisible'
      >
    >();

    for (const layer of [
      ...publicExternalLayers,
      ...orgExternalLayers,
      ...previewExternalLayers,
    ]) {
      merged.set(String(layer.id), layer);
    }

    return Array.from(merged.values());
  }, [orgExternalLayers, previewExternalLayers, publicExternalLayers]);

  useExternalLayerSync({
    mapView,
    externalLayers: externalLayersToSync,
    externalLayerVisibilityById,
    setExternalLayerVisibilityById,
    addedExternalLayersRef,
    setIsExternalLayersReady,
    setIsPublicExternalLayersReady,
    setIsProtectedExternalLayersReady,
    setExternalLayerLoadErrorById,
  });

  useDefaultLayerVisibilityById({
    layers: internalLayersWithVisibilityDefaults,
    setVisibilityById: setInternalLayerVisibilityById,
  });

  const clearLocationGraphicLayer = useCallback(() => {
    locationGraphicLayerRef.current.removeAll();
  }, []);

  const { drawLocationOnMap } = useLocationGraphicLayer({
    mapView,
    drawingLayerRef: locationGraphicLayerRef,
    clearDrawingLayer: clearLocationGraphicLayer,
  });

  useEffect(() => {
    if (!mapView?.map) return;

    const locationGraphicLayer = locationGraphicLayerRef.current;
    mapView.map.add(locationGraphicLayer);

    return () => {
      mapView.map.remove(locationGraphicLayer);
      clearLocationGraphicLayer();
    };
  }, [clearLocationGraphicLayer, mapView]);

  useEffect(() => {
    if (!incident?.location) {
      clearLocationGraphicLayer();
      return;
    }

    drawLocationOnMap(incident.location);
  }, [clearLocationGraphicLayer, drawLocationOnMap, incident?.location]);

  const getAddedExternalLayers = useCallback(
    (layerId: string) => addedExternalLayersRef.current.get(layerId),
    []
  );

  const toggleExternalLayerVisibility = useCallback(
    (layerId: string) => {
      const isVisible = !(externalLayerVisibilityById.get(layerId) ?? false);

      setExternalLayerVisibilityById((prev) =>
        new Map(prev).set(layerId, isVisible)
      );

      getAddedExternalLayers(layerId)?.forEach((layer) => {
        layer.visible = isVisible;
      });
    },
    [externalLayerVisibilityById, getAddedExternalLayers]
  );

  const isExternalLayerVisible = useCallback(
    (layerId: string) => externalLayerVisibilityById.get(layerId) ?? false,
    [externalLayerVisibilityById]
  );

  const toggleInternalLayerVisibility = useCallback((layerId: string) => {
    setInternalLayerVisibilityById((prev) => {
      const isVisible = prev.get(layerId) ?? false;
      return new Map(prev).set(layerId, !isVisible);
    });
  }, []);

  const isInternalLayerVisible = useCallback(
    (layerId: string) => internalLayerVisibilityById.get(layerId) ?? false,
    [internalLayerVisibilityById]
  );

  const setSelectedInternalLayerVersion = useCallback(
    (layerId: string, version: InternalLayerVersion) => {
      setSelectedInternalLayerVersionByInternalLayerId((prev) => ({
        ...prev,
        [layerId]: version,
      }));
    },
    []
  );

  const getSelectedVersionForLayer = useCallback(
    (layerId: string): InternalLayerVersion | undefined => {
      return selectedInternalLayerVersionByInternalLayerId[layerId];
    },
    [selectedInternalLayerVersionByInternalLayerId]
  );

  const toggleAorVisibility = useCallback(
    () => setIsAorVisible((prev) => !prev),
    []
  );

  const zoomToLocation = useCallback(
    ({
      longitude,
      latitude,
      zoom = 10,
    }: {
      longitude: number;
      latitude: number;
      zoom?: number;
    }) => zoomMapViewToLocation({ mapView, longitude, latitude, zoom }),
    [mapView]
  );

  const zoomToGraphic = useCallback(
    ({ graphic, zoom }: { graphic: __esri.Graphic; zoom?: number }) =>
      zoomMapViewToGraphic({ mapView, graphic, zoom }),
    [mapView]
  );

  const zoomToExternalLayer = useCallback(
    async ({ externalLayerId }: { externalLayerId: string }) => {
      if (!mapView) return;

      const layers = getAddedExternalLayers(externalLayerId) ?? [];
      if (layers.length === 0) return;

      const extents = await Promise.all(
        layers.map((l) => getLayerExtent(l, mapView))
      );
      const merged = extents
        .filter((e): e is __esri.Extent => e !== null)
        .reduce<__esri.Extent | null>(
          (acc, e) => (acc ? acc.union(e) : e),
          null
        );

      if (merged) {
        return mapView.goTo(merged.expand(1.2), { duration: 500 });
      }
    },
    [mapView, getAddedExternalLayers]
  );

  const zoomToInternalLayer = useCallback(
    ({ internalLayerId }: { internalLayerId: string }) => {
      if (!mapView) return;

      const selectedVersion = getSelectedVersionForLayer(internalLayerId);
      if (!selectedVersion) return;

      const graphicsLayer = internalGraphicsLayersRef.current.get(
        selectedVersion.id
      );
      if (!graphicsLayer) return;

      const graphics = graphicsLayer.graphics.toArray();
      if (graphics.length === 0) return;

      return mapView.goTo(graphics, { duration: 500 });
    },
    [getSelectedVersionForLayer, mapView]
  );

  const getExternalLayerFeatures = useCallback(
    async (externalLayerId: string) => {
      const layers = getAddedExternalLayers(externalLayerId);

      if (!layers?.length) {
        return [];
      }
      return queryExternalLayerFeatures(layers);
    },
    [getAddedExternalLayers]
  );

  const isExternalLayerLoadError = useCallback(
    (externalLayerId: string) =>
      externalLayerLoadErrorById.get(externalLayerId) ?? false,
    [externalLayerLoadErrorById]
  );

  const captureMapSketchFile = useCallback(async () => {
    if (!mapView) return;

    await mapView.when();

    const locationGraphics = locationGraphicLayerRef.current.graphics.toArray();
    if (locationGraphics.length === 0) return;

    // Clear padding to ensure we capture the entire map, then restore it afterwards.
    const previousPadding = { ...mapView.padding };
    mapView.set('padding', { left: 0, top: 0, right: 0, bottom: 0 });

    await mapView.goTo(locationGraphics);

    // Wait for the view to stop moving
    await reactiveUtil.whenOnce(() => mapView.stationary && !mapView.updating);

    const screenshot = await mapView.takeScreenshot({
      format: 'png',
      height: MAP_BENCH_HEIGHT,
      width: MAP_BENCH_WIDTH,
    });
    const response = await fetch(screenshot.dataUrl);
    const blob = await response.blob();

    mapView.set('padding', previousPadding);

    return new File([blob], 'mapSketch.png', { type: 'image/png' });
  }, [mapView]);

  // Auto-zoom to user's home AOR when map view and aor query has finished
  useEffect(() => {
    if (!homeAorId || isHomeAorDescendantsLoading) return;

    const descendantIds = homeAorDescendants?.map((d) => d.id);
    performZoomToHomeAor(mapView, aors, homeAorId, descendantIds, {
      currentAorFilterIds: aorFilterIds,
      isAorVisible,
      onFilterChange: setAorFilterIds,
      onVisibilityChange: setIsAorVisible,
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    mapView,
    aors,
    homeAorId,
    homeAorDescendants,
    isHomeAorDescendantsLoading,
    setIsAorVisible,
    setAorFilterIds,
  ]);

  // Setup offscreen div used as a \"bench\" container for initializing the USCG MapView/WebMap.
  useEffect(() => {
    const bench = document.getElementById(
      MAP_BENCH_ID
    ) as HTMLDivElement | null;
    if (!bench) return;

    const originalBenchStyle = bench.style.cssText;
    bench.style.cssText = `position:absolute;width:${MAP_BENCH_WIDTH}px;height:${MAP_BENCH_HEIGHT}px;overflow:hidden;left:-9999px;top:0`;

    const webMap =
      mapType === 'uscg'
        ? new WebMap({
            portalItem: { id: USCG_PORTAL_ITEM_ID },
          })
        : new WebMap({
            basemap: HYBRID,
          });

    const newMapView =
      mapType === 'hybrid'
        ? new MapView({
            map: webMap,
            container: bench,
            center: US_INITIAL_VIEW.center,
            zoom: US_INITIAL_VIEW.zoom,
          })
        : new MapView({
            map: webMap,
            container: bench,
          });

    // No padding needed — sidebar and map are flex siblings, not overlapping
    newMapView.set('padding', { left: 0, top: 0, right: 0, bottom: 0 });
    newMapView.when(() => {
      setMapView(newMapView);
    });

    return () => {
      newMapView.destroy();
      webMap.destroy();
      bench.style.cssText = originalBenchStyle;
      setMapView(null);
    };
  }, [mapType]);

  const value = {
    mapView,
    mapViewElementRef,
    isMapVisible,
    setIsMapVisible,
    mapType,
    setMapType,
    toggleExternalLayerVisibility,
    isExternalLayerVisible,
    toggleInternalLayerVisibility,
    isInternalLayerVisible,
    startDrawing,
    setPolygonDrawingColor,
    setPolygonDrawingOpacity,
    stopDrawing,
    addPendingPoint,
    addPendingPolyline,
    addPendingPolygon,
    clearPendingGraphics,
    removePendingGraphicsByPendingGraphicId,
    symbolDrawingState,
    connectDrawingPoints,
    resetDrawing,

    zoomToLocation,
    zoomToGraphic,
    zoomToExternalLayer,
    zoomToInternalLayer,
    getExternalLayerFeatures,

    isExternalLayerLoadError,
    externalLayerLoadErrorById,

    aorFilterIds,
    setAorFilterIds,

    incidentFilterId,
    setIncidentFilterId,

    toggleAorVisibility,
    isAorVisible,
    setIsAorVisible,

    setSelectedInternalLayerVersion,
    selectedInternalLayerVersionByInternalLayerId,
    getSelectedVersionForLayer,
    sidebarSelectedInternalLayer,
    setSidebarSelectedInternalLayer,
    isLoadingInternalFeatures,
    isArcGISAuthorized,
    isCheckingArcGISAuthorization,
    authorizeArcGIS,
    setIsArcGISAuthorized,
    isPublicExternalLayersReady,
    isProtectedExternalLayersReady:
      !isArcGISAuthorized || isProtectedExternalLayersReady,
    isExternalLayersReady,
    captureMapSketchFile,

    previewLayers,
    setPreviewLayers,
  };

  return (
    <UscgMapContext.Provider value={value}>{children}</UscgMapContext.Provider>
  );
};

/**
 * Resolves the map bounding box for a layer (e.g. coordinates we can use to
 * zoom-to-layer).
 */
async function getLayerExtent(
  layer: __esri.Layer,
  mapView: __esri.MapView
): Promise<__esri.Extent | null> {
  if (layer instanceof GroupLayer) {
    const childExtents = await Promise.all(
      layer.allLayers.toArray().map((child) => getLayerExtent(child, mapView))
    );
    return childExtents
      .filter((e): e is __esri.Extent => e !== null)
      .reduce<__esri.Extent | null>((acc, e) => (acc ? acc.union(e) : e), null);
  }

  if (layer instanceof FeatureLayer) {
    try {
      const { extent } = await layer.queryExtent({ where: '1=1' });
      if (extent) return extent;
    } catch (err) {
      console.error('Failed to get extent for FeatureLayer', err);
    }
  }

  if (layer instanceof MapImageLayer) {
    // MapImageLayer.fullExtent can include non-visible sublayers, so we merge only visible sublayer extents
    // to ensure zoom-to-layer matches what the user is currently viewing.
    const sublayers = layer.allSublayers?.toArray() ?? [];
    const visibleSubLayers = sublayers.filter((s) => s.visible);

    const sublayerExtents = await Promise.all(
      visibleSubLayers.map(async (sublayer) => {
        try {
          // createFeatureLayer() only works for queryable sublayers
          const featureLayer = await sublayer.createFeatureLayer();
          if (!featureLayer) return null;

          await featureLayer.load();
          // queryExtent() gives us the minimum bounding box that will cover all features in the sublayer
          const { extent } = await featureLayer.queryExtent({ where: '1=1' });
          featureLayer.destroy();
          if (extent) {
            return extent;
          }
        } catch (err) {
          console.error('Failed to get extent for MapImageLayer sublayer', err);
        }

        return null;
      })
    );
    // extent.union(nextExtent) gives us the minimum bounding box that will cover all features in all sublayers.
    const merged = sublayerExtents
      .filter((e): e is __esri.Extent => e !== null)
      .reduce<__esri.Extent | null>((acc, e) => (acc ? acc.union(e) : e), null);
    if (merged) {
      return merged;
    }
  }

  return layer.fullExtent ?? null;
}
